import React from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Star, ImagePlus, X, MessageSquare, AlertTriangle } from 'lucide-react';
import ComposerSheet from '../ui/ComposerSheet';

const CustomerDialogs = ({
  // Review Dialog
  showReviewDialog,
  setShowReviewDialog,
  selectedInquiry,
  setSelectedInquiry,
  reviewData,
  setReviewData,
  submitReview,
  loading,
  formatShortDate,
  uploadingReviewImage,
  handleReviewImageUpload,
  removeReviewImage,
  getImageUrl,
  
  // Dispute Dialog
  disputingInquiry,
  setDisputingInquiry,
  disputeData,
  setDisputeData,
  submitDispute,

  // Navigation
  navigate
}) => {
  const renderStars = (rating, interactive = false) => (
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map((star) => (
        <Star
          key={star}
          className={`h-8 w-8 ${star <= rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'} ${interactive ? 'cursor-pointer hover:scale-110 transition-transform' : ''}`}
          onClick={() => interactive && setReviewData({ ...reviewData, rating: star })}
        />
      ))}
    </div>
  );

  return (
    <>
      {/* ── Leave Review ComposerSheet ── */}
      <ComposerSheet
        open={showReviewDialog && !!selectedInquiry}
        onClose={() => { setShowReviewDialog(false); setSelectedInquiry(null); }}
        title="Leave a Review"
        subtitle={selectedInquiry ? `Share your experience for ${selectedInquiry.event_type}` : undefined}
        icon={<Star className="h-5 w-5 text-yellow-500" />}
        testId="review-composer"
        maxWidth="sm:max-w-lg"
        footer={
          <div className="flex gap-3">
            <Button variant="outline" onClick={() => { setShowReviewDialog(false); setSelectedInquiry(null); }} disabled={loading} className="flex-1">Cancel</Button>
            <Button onClick={submitReview} disabled={loading || !reviewData.comment.trim() || uploadingReviewImage} className="flex-1" data-testid="submit-review-btn">
              {loading ? 'Submitting...' : 'Submit Review'}
            </Button>
          </div>
        }
      >
        {selectedInquiry && (
          <div className="space-y-4">
            <div className="bg-emerald-50 p-3.5 rounded-xl border border-emerald-200">
              <p className="text-xs font-medium text-emerald-600 mb-0.5">Reviewing</p>
              <button
                type="button"
                onClick={() => { if (navigate && selectedInquiry.vendor_id) { setShowReviewDialog(false); setSelectedInquiry(null); navigate(`/vendors/${selectedInquiry.vendor_id}`); } }}
                className={`text-base font-bold ${selectedInquiry.vendor_id && navigate ? 'text-emerald-800 hover:text-emerald-900 hover:underline cursor-pointer' : 'text-emerald-800'}`}
                data-testid="review-vendor-link"
              >
                {selectedInquiry.vendor_name || 'Service Provider'}
              </button>
              <button
                type="button"
                onClick={() => { if (navigate && selectedInquiry.event_id) { setShowReviewDialog(false); setSelectedInquiry(null); navigate(`/events/${selectedInquiry.event_id}`); } }}
                className={`text-xs mt-0.5 block ${selectedInquiry.event_id && navigate ? 'text-emerald-700 hover:text-emerald-800 hover:underline cursor-pointer' : 'text-emerald-700'}`}
                data-testid="review-event-link"
              >
                {selectedInquiry.event_type} on {formatShortDate(selectedInquiry.event_date)}
              </button>
            </div>

            <div>
              <Label className="text-sm font-medium text-gray-700">Rating</Label>
              <div className="mt-2">{renderStars(reviewData.rating, true)}</div>
            </div>

            <div>
              <Label htmlFor="review-comment" className="text-sm font-medium text-gray-700">Your Review</Label>
              <Textarea
                id="review-comment"
                value={reviewData.comment}
                onChange={(e) => setReviewData({ ...reviewData, comment: e.target.value })}
                placeholder="Tell others about your experience..."
                rows={4}
                className="mt-1.5"
                data-testid="review-comment"
              />
            </div>

            <div>
              <Label className="text-sm font-medium text-gray-700">Add Photos <span className="font-normal text-gray-400">(optional, max 3)</span></Label>
              <p className="text-xs text-gray-500 mb-2">Share photos from your event</p>
              {reviewData.images && reviewData.images.length > 0 && (
                <div className="grid grid-cols-3 gap-2 mb-3">
                  {reviewData.images.map((image, idx) => (
                    <div key={idx} className="relative aspect-square">
                      <img src={getImageUrl ? getImageUrl(image) : image} alt={`Review ${idx + 1}`} className="w-full h-full object-cover rounded-lg border" />
                      <button type="button" onClick={() => removeReviewImage && removeReviewImage(idx)} className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"><X className="h-3 w-3" /></button>
                    </div>
                  ))}
                </div>
              )}
              {(!reviewData.images || reviewData.images.length < 3) && (
                <div className="flex items-center gap-2">
                  <Input type="file" accept=".jpg,.jpeg,.png" onChange={handleReviewImageUpload} disabled={uploadingReviewImage} className="flex-1" data-testid="review-image-upload" />
                  {uploadingReviewImage && <span className="text-sm text-gray-500">Uploading...</span>}
                </div>
              )}
              {reviewData.images && reviewData.images.length >= 3 && (
                <p className="text-xs text-amber-600 mt-1">Maximum 3 photos reached</p>
              )}
            </div>

            <div className="bg-yellow-50 border border-yellow-200 p-3 rounded-lg text-sm text-yellow-800">
              Your review will be visible after admin approval.
            </div>
          </div>
        )}
      </ComposerSheet>

      {/* ── Dispute ComposerSheet ── */}
      <ComposerSheet
        open={!!disputingInquiry}
        onClose={() => { setDisputingInquiry(null); setDisputeData({ reason: '', description: '' }); }}
        title="Dispute Request Status"
        subtitle="If the service was completed but marked as 'Did not move forward', please let us know"
        icon={<AlertTriangle className="h-5 w-5 text-amber-500" />}
        testId="dispute-composer"
        maxWidth="sm:max-w-md"
        footer={
          <div className="flex gap-3">
            <Button variant="outline" onClick={() => { setDisputingInquiry(null); setDisputeData({ reason: '', description: '' }); }} disabled={loading} className="flex-1">Cancel</Button>
            <Button onClick={submitDispute} disabled={loading || !disputeData.reason || !disputeData.description.trim()} className="flex-1" data-testid="submit-dispute-btn">
              {loading ? 'Submitting...' : 'Submit Dispute'}
            </Button>
          </div>
        }
      >
        <div className="space-y-4">
          <div>
            <Label htmlFor="dispute-reason">Reason *</Label>
            <Select value={disputeData.reason} onValueChange={(value) => setDisputeData({ ...disputeData, reason: value })}>
              <SelectTrigger id="dispute-reason" data-testid="dispute-reason-select"><SelectValue placeholder="Select a reason..." /></SelectTrigger>
              <SelectContent>
                <SelectItem value="service_completed">The service was completed but marked incorrectly</SelectItem>
                <SelectItem value="booking_confirmed">We went through with this booking</SelectItem>
                <SelectItem value="vendor_performed">Service provider performed the service</SelectItem>
                <SelectItem value="other">Other reason</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="dispute-description">Description *</Label>
            <Textarea
              id="dispute-description"
              value={disputeData.description}
              onChange={(e) => setDisputeData({ ...disputeData, description: e.target.value })}
              placeholder="Please explain what happened and why you believe this should be changed..."
              rows={4}
              data-testid="dispute-description"
            />
          </div>

          <div className="bg-blue-50 border border-blue-200 p-3 rounded-lg text-sm text-blue-800">
            <p className="font-medium mb-1">What happens next?</p>
            <p>An admin will review your dispute and may change the inquiry status to allow you to leave a review.</p>
          </div>
        </div>
      </ComposerSheet>
    </>
  );
};

export default CustomerDialogs;
